import type { ImmunizationGroup, ImmunizationGroupCode } from '@liza/fhir-utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The row title for an immunization group: the translated CDC vaccine-group name, or the source
 * text for rows that resolved to no group (disease history, unrecognised names). Every key is a
 * literal `t` call so extraction keeps both catalogs complete.
 */
export const useImmunizationGroupLabel = (): ((group: ImmunizationGroup) => string) => {
  const { t } = useTranslation('records');

  return useMemo(() => {
    const labels: Record<ImmunizationGroupCode, string> = {
      FLU: t('vaccineGroupFlu'),
      'COVID-19': t('vaccineGroupCovid19'),
      TDAP: t('vaccineGroupTdap'),
      Td: t('vaccineGroupTd'),
      PneumoPCV: t('vaccineGroupPneumoPcv'),
      PneumoPPV: t('vaccineGroupPneumoPpv'),
      ZOSTER: t('vaccineGroupZoster'),
      RSV: t('vaccineGroupRsv'),
      HepB: t('vaccineGroupHepB'),
      HepA: t('vaccineGroupHepA'),
      MMR: t('vaccineGroupMmr'),
      VARICELLA: t('vaccineGroupVaricella'),
      HPV: t('vaccineGroupHpv'),
      MENING: t('vaccineGroupMening'),
      MeningB: t('vaccineGroupMeningB'),
      DTAP: t('vaccineGroupDtap'),
      HIB: t('vaccineGroupHib'),
      POLIO: t('vaccineGroupPolio'),
      ROTAVIRUS: t('vaccineGroupRotavirus'),
      ADENO: t('vaccineGroupAdeno'),
      ANTHRAX: t('vaccineGroupAnthrax'),
      BCG: t('vaccineGroupBcg'),
      Chikungunya: t('vaccineGroupChikungunya'),
      cholera: t('vaccineGroupCholera'),
      DENGUE: t('vaccineGroupDengue'),
      Ebola: t('vaccineGroupEbola'),
      'FLU-Avian-H5': t('vaccineGroupAvianFluH5'),
      'H1N1 flu': t('vaccineGroupH1n1Flu'),
      'H5N1 flu': t('vaccineGroupH5n1Flu'),
      'Japanese encephalitis': t('vaccineGroupJapaneseEncephalitis'),
      'Lyme disease': t('vaccineGroupLymeDisease'),
      Orthopoxvirus: t('vaccineGroupOrthopoxvirus'),
      Plague: t('vaccineGroupPlague'),
      RABIES: t('vaccineGroupRabies'),
      'Tick-borne encephalitis': t('vaccineGroupTickBorneEncephalitis'),
      TYPHOID: t('vaccineGroupTyphoid'),
      VEE: t('vaccineGroupVee'),
      YELLOWFEVER: t('vaccineGroupYellowFever'),
      TST: t('vaccineGroupTst'),
      IG: t('vaccineGroupIg'),
    };
    return (group: ImmunizationGroup): string =>
      group.group ? labels[group.group] : (group.label ?? t('unknownImmunization'));
  }, [t]);
};
